// Fiók-detail „Szolgáltatások" szekció — a fiókra kötött szolgáltatás-hozzárendelések READ-ONLY listája,
// státusszal együtt (aktív ÉS függő — a sor jelvénye mondja meg, melyik).
//
// MIÉRT read-only: a hozzárendelés (szolgáltatás + csomag + gyakoriság + státusz) az ügyfélhez tartozik, és a
// Szolgáltatások modulban / az ügyfél-detailen szerkeszthető — a fiók csak a CÉLPONTJA. Egy „hozzáadás" gomb itt
// azt sugallná, hogy a fiókon lehet szolgáltatást létrehozni; a fiók-detail viszont a fiók ADATLAPJA, nem a
// szolgáltatás-kezelő. Ezért nincs sor-akció.
//
// ⚠️ AZ ADAT FORRÁSA MEGVÁLTOZOTT (user-döntés). Korábban a fiók-detail válaszába ágyazott
// `AccountRowSchema.services` volt, ami az Odin foundation-readjének projekciója: KIZÁRÓLAG a szolgáltatás
// NEVE, serviceId-ra dedupolva. Emiatt ugyanaz a hozzárendelés a hozzárendelés-listán „RoomSome Booking engine
// / RoomSome (jutalékos) / RoomSome jutalék (direkt)" volt, itt viszont csak „RoomSome Booking engine" — a
// kezelő nem tudta, ugyanarról a sorról van-e szó, és két azonos szolgáltatású, KÜLÖNBÖZŐ konstrukciójú sor
// egyetlen tételnek látszott. Ma a szekció a hozzárendelés-lista VÉGPONTJÁT kérdezi (`?accountId=`), és a
// neveket a KÖZÖS `assignmentSubLines` adja — ugyanaz, amit a lista első oszlopa használ.
//
// ⚠️ A STÁTUSZ-SZŰRÉS IS MEGSZŰNT ezzel: a régi projekció `status = 'active'`-ra szűrt, a lista-végpont nem.
// Élő példa (Hermin Hotel): 2 sor helyett 15, amiből 4 aktív és 11 FÜGGŐ. Ez nem rejtőzhet el — a függő
// hozzárendelés valós, és a „hamarosan induló" figyelem-panel külön figyelmeztet is rá —, a sor-jelvény
// pedig kimondja. Az ügyfél-adatlap azonos szekciója eddig is így viselkedett, tehát a kettő most egyezik.
//
// A JOG: a végpont `service_assignments.view`-t kér (nem védett jog; élesben mind az 57 belső fiók bírja),
// és az ügyfél-detail azonos szekciója már eddig is ezt hívta. Jog nélkül a szekció a `RelatedList` saját
// „nincs jogod" állapotát mutatja — nem tűnik el és nem is bukik el a lap.
import { type ServiceAssignmentRowSchema } from '@atlas/contracts';
import { Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { type TFunction } from 'i18next';
import { type z } from 'zod';

import {
  AssignmentInvoiceGroups,
  AssignmentViewToggle,
  type AssignmentSectionView,
} from '@/components/service-assignments/assignment-invoice-groups';
import { ServiceStatusBadge } from '@/components/service-assignments/service-status-badge';
import { FormSection } from '@/components/shared/form-section';
import { RelatedList, type RelatedListItem } from '@/components/shared/related-list';
import { ACCOUNT_SECTION } from '@/features/accounts/sections';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import { api } from '@/lib/api';
import { assignmentSubLines } from '@/lib/service-plan-label';
import { isForbiddenStatus } from '@/lib/error-status';

type ServiceAssignmentRow = z.infer<typeof ServiceAssignmentRowSchema>;

export function AccountServicesSection({
  accountId,
  clientId,
  t,
}: {
  accountId: string;
  /** A fiók ügyfele — a „Számlánként" nézet az ügyfél-szintű, a fiókot érintő hozzárendeléseket is mutatja. */
  clientId: string;
  t: TFunction;
}): JSX.Element {
  const navigate = useNavigate();
  // alapból a lapos lista (csak a fiók-szintű sorok); a „Számlánként" nézetben az ügyfél-szintű, a fiókot
  // érintő hozzárendelések is ott vannak, mert azok is erre a fiókra számlázódnak (user-döntés)
  const [view, setView] = useState<AssignmentSectionView>('list');

  const listKey = [...serviceAssignmentKeys.lists(), 'by-account', accountId];
  const query = api.serviceAssignments.useQuery(listKey, {
    query: { accountId, limit: 200 },
  });
  const rows: ServiceAssignmentRow[] =
    query.data?.status === 200 ? query.data.body.assignments : [];
  const errStatus = query.error?.status;

  /** A másodlagos sorok a KÖZÖS szabályból — a lista-oldal első oszlopával bitre azonos. */
  const subLines = (r: ServiceAssignmentRow): JSX.Element | undefined => {
    const lines = assignmentSubLines(r, t);
    if (lines.length === 0) return undefined;
    return (
      <>
        {lines.map((line) => (
          <div key={line} className="truncate" title={line}>
            {line}
          </div>
        ))}
      </>
    );
  };

  const items: RelatedListItem[] = rows.map((r) => ({
    id: r.id,
    title: r.serviceName ?? r.planName ?? '—',
    subtitle: subLines(r),
    // A fiók ADOTT (ez az ő lapja), ezért a fiók-név itt zaj lenne — a számlázási gyakoriság az, ami nem
    // derül ki máshonnan.
    meta: r.billingFrequency
      ? t(`serviceAssignments.frequencyLabel.${r.billingFrequency}`, {
          defaultValue: r.billingFrequency,
        })
      : undefined,
    status: (
      <ServiceStatusBadge
        status={r.status}
        label={t(`serviceAssignments.statusLabel.${r.status}`, { defaultValue: r.status })}
      />
    ),
  }));

  return (
    <FormSection
      id={ACCOUNT_SECTION.services}
      title={t('accounts.sections.services')}
      action={<AssignmentViewToggle value={view} onChange={setView} t={t} />}
    >
      {view === 'invoices' ? (
        <AssignmentInvoiceGroups target={{ clientId, accountId }} t={t} />
      ) : (
        <RelatedList
          items={items}
          isLoading={query.isLoading}
          isError={query.isError}
          forbidden={isForbiddenStatus(errStatus)}
          onRetry={() => void query.refetch()}
          // MIND A HOZZÁRENDELÉS LÁTSZIK (user-döntés): eddig 8 után csonkult. Élesben a legtöbb egy fiókon
          // 30 sor, az átlag 5,9 — kirenderelve is rövid szekció, és a kérdés („mit kap ez a fiók?") a
          // TELJES listát kívánja.
          maxVisible={Infinity}
          showAllLabel={(total) => t('accounts.relatedTotal', { count: total })}
          // A LÁB-LINK a FIÓKRA szűrt hozzárendelés-listára visz (`asg_account`) — ott vannak a szűrők, az
          // oszlopok és a sor-akciók.
          footerAction={{
            label: t('common.openFilteredList'),
            onClick: () =>
              void navigate({
                to: '/services',
                search: { tab: 'assignments', asg_account: accountId },
              }),
          }}
          emptyLabel={t('accounts.form.servicesEmpty')}
          emptyIcon={Square3Stack3DIcon}
          errorLabel={t('accounts.relatedError')}
          forbiddenLabel={t('accounts.relatedForbidden')}
          retryLabel={t('accounts.relatedRetry')}
        />
      )}
    </FormSection>
  );
}
